import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FEEDBACK_STATUS } from '../../constants/feedback';
import { useUpdateFeedbackReport } from '../../hooks/useFeedback';
import { type FeedbackReport } from '../../api/feedback';
import { apiErrorDetail } from '../../utils/apiError';

interface FeedbackTriageEditorOptions {
  reportId: string;
  /** The full record; the editable fields are (re)seeded when it arrives. */
  report: FeedbackReport | undefined;
  /** Success toast when the save also marked the report processed. */
  closedToast: string;
  /** Seed shown before the full record arrives (e.g. from the list row). */
  initialTicketRef?: string;
}

/**
 * State and save path shared by both triage views (the report modal and
 * the expanding ticket card), so the PATCH contract and its toasts live
 * in exactly one place.
 */
export function useFeedbackTriageEditor({
  reportId,
  report,
  closedToast,
  initialTicketRef = '',
}: FeedbackTriageEditorOptions) {
  const update = useUpdateFeedbackReport();
  const [ticketRef, setTicketRef] = useState(initialTicketRef);
  const [note, setNote] = useState('');

  // Keyed on the id rather than the fields themselves, so a refetch does
  // not undo the triager's in-progress edits.
  useEffect(() => {
    if (report) {
      setTicketRef(report.ticket_ref ?? '');
      setNote(report.resolution_note ?? '');
    }
  }, [report?.id]);

  // Until the full record has arrived, local state holds only the list-row
  // seed — `note` is still ''. Sending the editable fields from that state
  // would PATCH `null` over whatever another triager had written, so before
  // the seeding effect above has run a save carries the status alone.
  const isReady = report !== undefined;

  const save = (status?: number): void => {
    // Nothing to send: no status change, and no editable fields we are
    // allowed to trust yet. The server would reject an empty patch anyway.
    if (!isReady && status === undefined) return;
    update.mutate(
      {
        id: reportId,
        changes: {
          ...(isReady
            ? {
                ticket_ref: ticketRef.trim() || null,
                resolution_note: note.trim() || null,
              }
            : {}),
          ...(status === undefined ? {} : { status }),
        },
      },
      {
        onSuccess: () => toast.success(status === FEEDBACK_STATUS.PROCESSED ? closedToast : 'Saved'),
        onError: (error) => toast.error(`Could not save: ${apiErrorDetail(error)}`),
      },
    );
  };

  return { ticketRef, setTicketRef, note, setNote, save, isReady, isSaving: update.isPending };
}
